import { CommonModule } from "@angular/common";
import {
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  inject,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { Router } from "@angular/router";
import { Subscription } from "rxjs";
import { LucideDynamicIcon } from "@lucide/angular";
import { backend } from "@/integrations/backend/client";
import { AuthService, Profile, User } from "../core/auth.service";
import { loadDailyStreakBonusTotals } from "../core/daily-streak-bonus";
import { repairCurrentUserProgressAndBadges } from "../game/game-progress-sync";
import { HUB_OPTIONS, hubLabel, normalizeHub, type HubKey } from "../core/hubs";
import { HubFlagComponent } from "../shared/hub-flag.component";
import { badgeClasses, buttonClasses, cardClasses } from "../shared/ui";

interface BadgeItem {
  id: string;
  slug: string;
  name: string;
  description: string;
  icon: string | null;
}

interface UserBadge {
  badge_id: string;
  earned_at: string;
}

interface LeaderboardEntry {
  user_id: string;
  display_name: string | null;
  avatar_url: string | null;
  hub: string | null;
  total_score: number;
  islands_completed: number;
}

interface CrewLeaderboardEntry {
  crew_id: string;
  crew_name: string;
  owner_id: string;
  member_count: number;
  total_score: number;
  islands_completed: number;
  crew_level: number;
}

interface CrewMemberProgress {
  user_id: string;
  display_name: string | null;
  total_score: number;
}

@Component({
  selector: "app-achievements-page",
  standalone: true,
  imports: [CommonModule, FormsModule, LucideDynamicIcon, HubFlagComponent],
  template: `
    <div
      *ngIf="loading"
      class="min-h-screen flex items-center justify-center ocean-gradient"
    >
      <svg
        lucideIcon="loader-circle"
        class="h-8 w-8 animate-spin text-primary-foreground"
      ></svg>
    </div>

    <div *ngIf="!loading" class="min-h-screen ocean-gradient">
      <header
        class="sticky top-0 z-50 bg-background/80 backdrop-blur-md border-b border-border"
      >
        <div class="container max-w-4xl mx-auto px-4 py-4">
          <div class="flex items-center gap-4">
            <button
              [class]="buttonClasses('ghost', 'icon')"
              (click)="router.navigateByUrl('/')"
            >
              <svg lucideIcon="arrow-left" class="h-5 w-5"></svg>
            </button>
            <div class="flex items-center gap-3">
              <div class="p-2 rounded-full bg-secondary/20">
                <svg
                  lucideIcon="trophy"
                  class="h-6 w-6 text-secondary-foreground"
                ></svg>
              </div>
              <h1 class="text-xl font-bold text-foreground">Achievements</h1>
            </div>
          </div>
        </div>
      </header>

      <main class="container max-w-4xl mx-auto px-4 py-8">
        <div class="space-y-6">
          <div class="grid w-full grid-cols-4 rounded-md bg-muted p-1">
            <button
              type="button"
              (click)="activeTab = 'leaderboard'"
              [class]="tabClass('leaderboard')"
            >
              <svg lucideIcon="crown" class="h-4 w-4"></svg>
              Global
            </button>
            <button
              type="button"
              (click)="activeTab = 'hub'"
              [class]="tabClass('hub')"
            >
              <svg lucideIcon="flag" class="h-4 w-4"></svg>
              Hub
            </button>
            <button
              type="button"
              (click)="activeTab = 'crews'"
              [class]="tabClass('crews')"
            >
              <svg lucideIcon="users" class="h-4 w-4"></svg>
              Crews
            </button>
            <button
              type="button"
              (click)="activeTab = 'badges'"
              [class]="tabClass('badges')"
            >
              <svg lucideIcon="trophy" class="h-4 w-4"></svg>
              Badges
            </button>
          </div>

          <section *ngIf="activeTab === 'leaderboard'" class="space-y-6">
            <div
              *ngIf="currentUserRank > 0"
              [class]="
                cardClasses(
                  'border-primary bg-primary/5 animate-in slide-in-from-bottom-4 fade-in'
                )
              "
            >
              <div class="p-6">
                <div class="flex items-center justify-between flex-wrap gap-4">
                  <div class="flex items-center gap-4">
                    <div class="text-4xl font-bold text-primary">
                      #{{ currentUserRank }}
                    </div>
                    <div>
                      <p class="font-medium text-foreground">
                        Your Global Rank
                      </p>
                      <p class="text-sm text-muted-foreground">
                        Keep exploring islands to climb the leaderboard!
                      </p>
                    </div>
                  </div>
                  <div class="flex items-center gap-6">
                    <div
                      *ngIf="crewName && currentUserCrewRank > 0"
                      class="text-center border-r border-border pr-6"
                    >
                      <div class="flex items-center gap-2 text-accent">
                        <svg lucideIcon="users" class="h-4 w-4"></svg>
                        <span class="text-2xl font-bold"
                          >#{{ currentUserCrewRank }}</span
                        >
                      </div>
                      <p class="text-xs text-muted-foreground">
                        in {{ crewName }}
                      </p>
                    </div>
                    <div class="text-right">
                      <p class="text-2xl font-bold text-primary">
                        {{ leaderboard[currentUserRank - 1]?.total_score || 0 }}
                      </p>
                      <p class="text-sm text-muted-foreground">points</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div
              [class]="cardClasses('animate-in slide-in-from-bottom-4 fade-in')"
            >
              <header class="flex flex-col space-y-1.5 p-6">
                <h2
                  class="text-2xl font-semibold leading-none tracking-normal flex items-center gap-2"
                >
                  <svg
                    lucideIcon="crown"
                    class="h-5 w-5 text-secondary-foreground"
                  ></svg>
                  Top Explorers
                </h2>
                <p class="text-sm text-muted-foreground">
                  Adventurers ranked by total points earned
                </p>
              </header>
              <div class="p-6 pt-0 space-y-3">
                <div
                  *ngIf="leaderboard.length === 0"
                  class="text-center py-8 text-muted-foreground"
                >
                  <p>No explorers on the leaderboard yet!</p>
                  <p class="text-sm">Complete islands to be the first.</p>
                </div>

                <div
                  *ngFor="let entry of leaderboard; let index = index"
                  class="flex items-center gap-4 p-4 rounded-lg border animate-in slide-in-from-left-4 fade-in"
                  [ngClass]="
                    entry.user_id === user?.id
                      ? 'bg-primary/10 border-primary'
                      : 'bg-card border-border'
                  "
                >
                  <div [class]="rankClass(index + 1)">
                    <svg
                      *ngIf="index === 0"
                      lucideIcon="crown"
                      class="h-4 w-4"
                    ></svg>
                    <span *ngIf="index !== 0">{{ index + 1 }}</span>
                  </div>
                  <div
                    class="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center text-lg overflow-hidden"
                  >
                    <img
                      *ngIf="entry.avatar_url"
                      [src]="entry.avatar_url"
                      alt=""
                      class="w-full h-full rounded-full object-cover"
                    />
                    <span *ngIf="!entry.avatar_url">🧑‍✈️</span>
                  </div>
                  <div class="flex-1 min-w-0">
                    <div class="flex items-center gap-2 flex-wrap">
                      <p class="font-medium truncate">
                        {{ entry.display_name || "Explorer" }}
                      </p>
                      <span
                        *ngIf="entry.user_id === user?.id"
                        [class]="badgeClasses('outline', 'text-xs')"
                        >You</span
                      >
                      <span [class]="badgeClasses('outline', 'text-xs gap-1')">
                        <app-hub-flag [hub]="entry.hub" size="sm" />
                        {{ hubLabel(entry.hub) }}
                      </span>
                      <span
                        *ngIf="crewRank(entry.user_id) > 0 && crewName"
                        [class]="badgeClasses('secondary', 'text-xs gap-1')"
                      >
                        <svg lucideIcon="users" class="h-3 w-3"></svg>
                        #{{ crewRank(entry.user_id) }} in crew
                      </span>
                    </div>
                    <div
                      class="flex items-center gap-1 text-sm text-muted-foreground"
                    >
                      <svg lucideIcon="map-pin" class="h-3 w-3"></svg>
                      {{ entry.islands_completed }}/{{ totalIslands }} islands
                    </div>
                  </div>
                  <div class="text-right">
                    <p class="text-lg font-bold text-primary">
                      {{ entry.total_score }}
                    </p>
                    <p class="text-xs text-muted-foreground">points</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section *ngIf="activeTab === 'hub'" class="space-y-6">
            <div
              [class]="cardClasses('animate-in slide-in-from-bottom-4 fade-in')"
            >
              <header
                class="flex flex-col gap-4 p-6 md:flex-row md:items-center md:justify-between"
              >
                <div>
                  <h2
                    class="text-2xl font-semibold leading-none tracking-normal flex items-center gap-2"
                  >
                    <svg lucideIcon="flag" class="h-5 w-5 text-primary"></svg>
                    Hub Board
                  </h2>
                  <p class="mt-2 text-sm text-muted-foreground">
                    Explorers ranked inside each hub
                  </p>
                </div>
                <label
                  class="flex items-center gap-2 rounded-md border border-border bg-background px-3 py-2 text-sm"
                >
                  <app-hub-flag [hub]="selectedHub" size="sm" />
                  <select
                    class="bg-transparent outline-none"
                    [(ngModel)]="selectedHub"
                    (ngModelChange)="fetchCountryLeaderboard()"
                  >
                    <option *ngFor="let hub of hubOptions" [value]="hub.key">
                      {{ hub.label }}
                    </option>
                  </select>
                </label>
              </header>
              <div class="p-6 pt-0 space-y-3">
                <div
                  *ngIf="countryLeaderboard.length === 0"
                  class="text-center py-8 text-muted-foreground"
                >
                  <p>No explorers in {{ hubLabel(selectedHub) }} yet.</p>
                  <p class="text-sm">
                    Complete stages to put your hub on the board.
                  </p>
                </div>

                <div
                  *ngFor="let entry of countryLeaderboard; let index = index"
                  class="flex items-center gap-4 p-4 rounded-lg border animate-in slide-in-from-left-4 fade-in"
                  [ngClass]="
                    entry.user_id === user?.id
                      ? 'bg-primary/10 border-primary'
                      : 'bg-card border-border'
                  "
                >
                  <div [class]="rankClass(index + 1)">
                    <svg
                      *ngIf="index === 0"
                      lucideIcon="crown"
                      class="h-4 w-4"
                    ></svg>
                    <span *ngIf="index !== 0">{{ index + 1 }}</span>
                  </div>
                  <div
                    class="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center text-lg overflow-hidden"
                  >
                    <img
                      *ngIf="entry.avatar_url"
                      [src]="entry.avatar_url"
                      alt=""
                      class="w-full h-full rounded-full object-cover"
                    />
                    <span *ngIf="!entry.avatar_url">🧑‍✈️</span>
                  </div>
                  <div class="flex-1 min-w-0">
                    <div class="flex items-center gap-2 flex-wrap">
                      <app-hub-flag [hub]="entry.hub" size="sm" />
                      <p class="font-medium truncate">
                        {{ entry.display_name || "Explorer" }}
                      </p>
                      <span
                        *ngIf="entry.user_id === user?.id"
                        [class]="badgeClasses('outline', 'text-xs')"
                        >You</span
                      >
                    </div>
                    <div
                      class="flex items-center gap-1 text-sm text-muted-foreground"
                    >
                      <svg lucideIcon="map-pin" class="h-3 w-3"></svg>
                      {{ entry.islands_completed }}/{{ totalIslands }} islands
                    </div>
                  </div>
                  <div class="text-right">
                    <p class="text-lg font-bold text-primary">
                      {{ entry.total_score }}
                    </p>
                    <p class="text-xs text-muted-foreground">points</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section *ngIf="activeTab === 'crews'" class="space-y-6">
            <div
              [class]="cardClasses('animate-in slide-in-from-bottom-4 fade-in')"
            >
              <header class="flex flex-col space-y-1.5 p-6">
                <h2
                  class="text-2xl font-semibold leading-none tracking-normal flex items-center gap-2"
                >
                  <svg lucideIcon="users" class="h-5 w-5 text-accent"></svg>
                  Crew Board
                </h2>
                <p class="text-sm text-muted-foreground">
                  Crews ranked by total points earned together
                </p>
              </header>
              <div class="p-6 pt-0 space-y-3">
                <div
                  *ngIf="crewLeaderboard.length === 0"
                  class="text-center py-8 text-muted-foreground"
                >
                  <p>No crews on the board yet.</p>
                  <p class="text-sm">Create a crew and score together.</p>
                </div>

                <div
                  *ngFor="let crewEntry of crewLeaderboard; let index = index"
                  class="flex items-center gap-4 p-4 rounded-lg border bg-card border-border animate-in slide-in-from-left-4 fade-in"
                >
                  <div [class]="rankClass(index + 1)">
                    <svg
                      *ngIf="index === 0"
                      lucideIcon="crown"
                      class="h-4 w-4"
                    ></svg>
                    <span *ngIf="index !== 0">{{ index + 1 }}</span>
                  </div>
                  <div
                    class="grid h-10 w-10 place-items-center rounded-full bg-accent/15 text-accent"
                  >
                    <svg lucideIcon="ship" class="h-5 w-5"></svg>
                  </div>
                  <div class="flex-1 min-w-0">
                    <p class="font-medium truncate">
                      {{ crewEntry.crew_name }}
                    </p>
                    <div
                      class="flex flex-wrap items-center gap-3 text-sm text-muted-foreground"
                    >
                      <span class="inline-flex items-center gap-1">
                        <svg lucideIcon="users" class="h-3 w-3"></svg>
                        {{ crewEntry.member_count }} members
                      </span>
                      <span class="inline-flex items-center gap-1">
                        <svg lucideIcon="map-pin" class="h-3 w-3"></svg>
                        Level {{ crewEntry.crew_level }}
                      </span>
                    </div>
                  </div>
                  <div class="text-right">
                    <p class="text-lg font-bold text-primary">
                      {{ crewEntry.total_score }}
                    </p>
                    <p class="text-xs text-muted-foreground">points</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section *ngIf="activeTab === 'badges'" class="space-y-6">
            <div
              [class]="
                cardClasses(
                  'text-center py-8 animate-in slide-in-from-bottom-4 fade-in'
                )
              "
            >
              <div class="p-6">
                <div class="text-6xl mb-4">🏆</div>
                <h2 class="text-3xl font-bold text-foreground mb-2">
                  {{ earnedCount }} / {{ badges.length }}
                </h2>
                <p class="text-muted-foreground">Badges Earned</p>
              </div>
            </div>

            <div class="grid gap-4 md:grid-cols-2">
              <div
                *ngFor="let badge of badges"
                [class]="
                  cardClasses(
                    isBadgeEarned(badge.id)
                      ? 'transition-all border-secondary'
                      : 'transition-all opacity-60'
                  )
                "
              >
                <header class="flex flex-col space-y-1.5 p-6 pb-2">
                  <div class="flex items-start gap-4">
                    <div
                      class="text-4xl"
                      [class.grayscale]="!isBadgeEarned(badge.id)"
                    >
                      {{ badge.icon || "🎖️" }}
                    </div>
                    <div class="flex-1">
                      <div class="flex items-center gap-2 mb-1">
                        <h3
                          class="text-lg font-semibold leading-none tracking-normal"
                        >
                          {{ badge.name }}
                        </h3>
                        <span
                          *ngIf="isBadgeEarned(badge.id)"
                          [class]="badgeClasses('secondary', 'text-xs')"
                          >Earned</span
                        >
                        <svg
                          *ngIf="!isBadgeEarned(badge.id)"
                          lucideIcon="lock"
                          class="h-4 w-4 text-muted-foreground"
                        ></svg>
                      </div>
                      <p class="text-sm text-muted-foreground">
                        {{ badge.description }}
                      </p>
                      <p
                        *ngIf="earnedDate(badge.id)"
                        class="text-xs text-muted-foreground mt-2"
                      >
                        Earned on {{ earnedDate(badge.id) | date }}
                      </p>
                    </div>
                  </div>
                </header>
              </div>
            </div>
          </section>
        </div>
      </main>
    </div>
  `,
})
export class AchievementsPageComponent implements OnInit, OnDestroy {
  private readonly auth = inject(AuthService);
  readonly router = inject(Router);
  private readonly changeDetector = inject(ChangeDetectorRef);

  user: User | null = null;
  profile: Profile | null = null;
  badges: BadgeItem[] = [];
  userBadges: UserBadge[] = [];
  leaderboard: LeaderboardEntry[] = [];
  countryLeaderboard: LeaderboardEntry[] = [];
  crewLeaderboard: CrewLeaderboardEntry[] = [];
  crewMembers: CrewMemberProgress[] = [];
  crewName: string | null = null;
  totalIslands = 6;
  loading = true;
  activeTab: "leaderboard" | "hub" | "crews" | "badges" = "leaderboard";
  selectedHub: HubKey = "brasil";
  readonly hubOptions = HUB_OPTIONS;
  private subscription?: Subscription;

  ngOnInit() {
    this.subscription = this.auth.state$.subscribe((state) => {
      this.user = state.user;
      this.profile = state.profile;
      this.selectedHub = normalizeHub(state.profile?.hub) || this.selectedHub;
      this.changeDetector.detectChanges();
      void this.fetchData();
    });
  }

  ngOnDestroy() {
    this.subscription?.unsubscribe();
  }

  get earnedBadgeIds() {
    return new Set(this.userBadges.map((userBadge) => userBadge.badge_id));
  }

  get earnedCount() {
    return this.earnedBadgeIds.size;
  }

  get currentUserRank() {
    return (
      this.leaderboard.findIndex((entry) => entry.user_id === this.user?.id) + 1
    );
  }

  get currentUserCrewRank() {
    return (
      this.crewMembers.findIndex((member) => member.user_id === this.user?.id) +
      1
    );
  }

  async fetchData() {
    if (!this.user) return;
    this.loading = true;
    this.changeDetector.detectChanges();

    try {
      const repairedUserBadges = await repairCurrentUserProgressAndBadges(
        this.user.id,
      );

      const [
        badgesResponse,
        leaderboardResponse,
        countryLeaderboardResponse,
        crewLeaderboardResponse,
        islandsResponse,
      ] = await Promise.all([
        backend.from("badges").select("*"),
        backend.rpc("get_leaderboard"),
        backend.rpc("get_country_leaderboard", { p_hub: this.selectedHub }),
        backend.rpc("get_crew_leaderboard"),
        backend.from("islands").select("*", { count: "exact", head: true }),
      ]);

      this.badges = badgesResponse.data || [];
      this.userBadges = repairedUserBadges;
      this.leaderboard = (
        (leaderboardResponse.data as LeaderboardEntry[]) || []
      ).sort((a, b) => b.total_score - a.total_score);
      this.countryLeaderboard = (
        (countryLeaderboardResponse.data as LeaderboardEntry[]) || []
      ).sort((a, b) => b.total_score - a.total_score);
      this.crewLeaderboard = (
        (crewLeaderboardResponse.data as CrewLeaderboardEntry[]) || []
      ).sort((a, b) => b.total_score - a.total_score);
      this.totalIslands = islandsResponse.count || 6;

      const { data: membershipData } = await backend
        .from("crew_members")
        .select("crew_id")
        .eq("user_id", this.user.id)
        .maybeSingle();

      if (membershipData) {
        const { data: crewData } = await backend
          .from("crews")
          .select("name")
          .eq("id", membershipData.crew_id)
          .single();
        if (crewData) this.crewName = crewData.name;

        const { data: membersData } = await backend
          .from("crew_members")
          .select("user_id")
          .eq("crew_id", membershipData.crew_id);

        if (membersData && membersData.length > 0) {
          const memberIds: string[] = membersData.map(
            (member: { user_id: string }) => member.user_id,
          );
          const { data: profilesData } = await backend
            .from("profiles")
            .select("id, display_name")
            .in("id", memberIds);
          const { data: progressData } = await backend
            .from("island_progress")
            .select("user_id, score")
            .in("user_id", memberIds);
          const dailyBonusResult = await loadDailyStreakBonusTotals(memberIds);
          const dailyBonusTotals = dailyBonusResult.totals;
          const scoresMap = new Map<string, number>();

          progressData?.forEach(
            (progress: { user_id: string; score: number }) => {
              scoresMap.set(
                progress.user_id,
                (scoresMap.get(progress.user_id) || 0) + (progress.score || 0),
              );
            },
          );

          this.crewMembers = memberIds
            .map(
              (userId: string): CrewMemberProgress => ({
                user_id: userId,
                display_name:
                  profilesData?.find(
                    (profile: { id: string }) => profile.id === userId,
                  )?.display_name || null,
                total_score:
                  (scoresMap.get(userId) || 0) +
                  (dailyBonusTotals.get(userId) ?? 0),
              }),
            )
            .sort((a, b) => b.total_score - a.total_score);
        }
      }
    } catch (error) {
      console.error("Error fetching achievements:", error);
    } finally {
      this.loading = false;
      this.changeDetector.detectChanges();
    }
  }

  async fetchCountryLeaderboard() {
    const { data, error } = await backend.rpc("get_country_leaderboard", {
      p_hub: this.selectedHub,
    });
    if (error) {
      console.error("Error fetching hub leaderboard:", error);
      return;
    }
    this.countryLeaderboard = ((data as LeaderboardEntry[]) || []).sort(
      (a, b) => b.total_score - a.total_score,
    );
  }

  tabClass(value: "leaderboard" | "hub" | "crews" | "badges") {
    return [
      "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium transition-all",
      this.activeTab === value
        ? "bg-background text-foreground shadow-sm"
        : "text-muted-foreground hover:text-foreground",
    ].join(" ");
  }

  rankClass(rank: number) {
    if (rank === 1)
      return "flex items-center justify-center w-8 h-8 rounded-full bg-secondary text-secondary-foreground font-bold text-sm";
    if (rank === 2)
      return "flex items-center justify-center w-8 h-8 rounded-full bg-muted text-foreground font-bold text-sm";
    if (rank === 3)
      return "flex items-center justify-center w-8 h-8 rounded-full bg-destructive/20 text-destructive font-bold text-sm";
    return "flex items-center justify-center w-8 h-8 rounded-full bg-muted text-foreground font-bold text-sm";
  }

  crewRank(userId: string) {
    return (
      this.crewMembers.findIndex((member) => member.user_id === userId) + 1
    );
  }

  isBadgeEarned(badgeId: string) {
    return this.earnedBadgeIds.has(badgeId);
  }

  earnedDate(badgeId: string) {
    return (
      this.userBadges.find((badge) => badge.badge_id === badgeId)?.earned_at ||
      null
    );
  }

  buttonClasses = buttonClasses;
  cardClasses = cardClasses;
  badgeClasses = badgeClasses;
  hubLabel = hubLabel;
}
